/**
 * LoadingSpinner — Accessible loading indicator.
 *
 * Renders a dot-pulse animation with `role="status"` and
 * `aria-live="polite"` so screen readers announce the loading state
 * without interrupting the user. Honours `prefers-reduced-motion` —
 * the dot stays static instead of pulsing when the user has requested
 * reduced motion.
 *
 * Replaces ad-hoc spinners that several demos defined inline without
 * any of the accessibility scaffolding.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { useReducedMotion } from "../hooks/useReducedMotion.js";

interface LoadingSpinnerProps {
  readonly label?: string;
  /** Color of the dot. Falls back to a slate accent. */
  readonly color?: string;
  /** Display compactly (10 px dot, 0.75 rem label) versus default (14 px dot, 0.875 rem label). */
  readonly compact?: boolean;
  /** Optional className that wraps the entire component. */
  readonly className?: string;
}

const KEYFRAMES_ID = "csk-demo-ui-loading-spinner-keyframes";

function ensureKeyframes(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(KEYFRAMES_ID) !== null) return;
  const style = document.createElement("style");
  style.id = KEYFRAMES_ID;
  style.textContent =
    "@keyframes csk-loading-dot-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }";
  document.head.appendChild(style);
}

export function LoadingSpinner({
  label = "Loading…",
  color = "#2563eb",
  compact = false,
  className,
}: LoadingSpinnerProps): React.JSX.Element {
  const reducedMotion = useReducedMotion();
  React.useEffect(() => {
    if (!reducedMotion) ensureKeyframes();
  }, [reducedMotion]);

  const size = compact ? 10 : 14;
  return (
    <div
      role="status"
      aria-live="polite"
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.5rem",
        fontSize: compact ? "0.75rem" : "0.875rem",
        color: "currentColor",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: "inline-block",
          width: size,
          height: size,
          borderRadius: "50%",
          background: color,
          animation: reducedMotion ? undefined : "csk-loading-dot-pulse 1.2s ease-in-out infinite",
        }}
      />
      <span>{label}</span>
    </div>
  );
}
